import { useMemo, useState } from "react";
import {
  useGetStockLevelsQuery,
  useGetProductsQuery,
  useGetWarehousesQuery,
} from "../../../../redux/api";
import type { StockLevel } from "../../../../redux/api";
import { IconSearch, IconEdit, IconEye } from "../../../../components/icons";
import { StockAdjustmentFormModal } from "../stock-adjustment/stockAdjustment";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 5;

// Inventory → Stock Levels (list page). Read-only — StockLevel rows only ever
// come into existence via an adjustStock mutation, so there's no create/edit
// form for this entity itself; the "Adjust" action per row jumps to the Stock
// Adjustment create form pre-filled with that product+warehouse pair.
const StockLevels = () => {
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [prefillProductId, setPrefillProductId] = useState<string | undefined>(undefined);
  const [prefillWarehouseId, setPrefillWarehouseId] = useState<string | undefined>(undefined);

  const {
    data: stockLevels = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetStockLevelsQuery();
  const { data: products = [] } = useGetProductsQuery();
  const { data: warehouses = [] } = useGetWarehousesQuery();

  const [search, setSearch] = useState("");

  const productsById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const warehousesById = useMemo(() => new Map(warehouses.map((w) => [w.id, w])), [warehouses]);

  const rows = useMemo(
    () =>
      stockLevels.map((s) => ({
        ...s,
        productName: productsById.get(s.productId)?.productName ?? s.productId,
        productSku: productsById.get(s.productId)?.sku ?? "",
        warehouseName: warehousesById.get(s.warehouseId)?.name ?? s.warehouseId,
      })),
    [stockLevels, productsById, warehousesById],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.productName, r.productSku, r.warehouseName].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [rows, search]);

  const openAdjustFor = (productId: string, warehouseId: string) => {
    setPrefillProductId(productId);
    setPrefillWarehouseId(warehouseId);
    setAdjustModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = rows.find((r) => r.id === viewingId) ?? null;


  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load stock levels"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Stock Levels"
          onBack={() => setViewingId(null)}
          title={`${viewing.productName} — ${viewing.warehouseName}`}
          id={viewing.id}
          onEdit={() => openAdjustFor(viewing.productId, viewing.warehouseId)}
        >
          <OverviewFieldList
            fields={[
              { label: "Product", value: viewing.productName },
              { label: "Warehouse", value: viewing.warehouseName },
              { label: "Quantity", value: viewing.quantity },
              { label: "Last updated", value: new Date(viewing.updatedAt).toLocaleString() },
            ]}
          />
        </EntityOverview>
        <StockAdjustmentFormModal
          open={adjustModalOpen}
          onClose={() => setAdjustModalOpen(false)}
          prefillProductId={prefillProductId}
          prefillWarehouseId={prefillWarehouseId}
        />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Stock Levels</h2>
            <p className="panel__sub">
              Showing {visible.length} of {stockLevels.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search by product or warehouse…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                setPrefillProductId(undefined);
                setPrefillWarehouseId(undefined);
                setAdjustModalOpen(true);
              }}
            >
              New adjustment
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Warehouse</th>
                <th>Quantity</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading stock levels…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {stockLevels.length === 0
                      ? "No stock levels yet — make a stock adjustment to create one."
                      : "No stock levels match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((row: StockLevel & { productName: string; productSku: string; warehouseName: string }) => (
                  <tr key={row.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(row.id)}
                      >
                        {row.productName}
                      </button>
                    </td>
                    <td>{row.warehouseName}</td>
                    <td>
                      <span className="pill pill--neutral">{row.quantity}</span>
                    </td>
                    <td className="cell-date">{new Date(row.updatedAt).toLocaleString()}</td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(row.id),
                          },
                          {
                            label: "Adjust stock",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openAdjustFor(row.productId, row.warehouseId),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <StockAdjustmentFormModal
        open={adjustModalOpen}
        onClose={() => setAdjustModalOpen(false)}
        prefillProductId={prefillProductId}
        prefillWarehouseId={prefillWarehouseId}
      />
    </div>
  );
};

export default StockLevels;
